import React from "react";
import { AlertTriangle, CalendarClock, CreditCard, X } from "lucide-react";
import { useSelector } from "react-redux";
import { useAuth, useCustomerData, useUserRole } from "../../hooks/useAuth";
import { useTheme } from "./ThemeProvider";

const toDateKey = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseDateKey = (value) => {
  const [year, month, day] = String(value || "").slice(0, 10).split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const formatDate = (value) => {
  const date = parseDateKey(value);
  if (!date) return "Not set";
  return date.toLocaleDateString("en-PH", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const getCountdown = (endDate) => {
  const end = parseDateKey(endDate);
  if (!end) return null;
  const today = parseDateKey(toDateKey());
  const days = Math.round((end - today) / 86400000);
  const reminderWindowDays = 10;

  if (days < 0) {
    const elapsed = Math.abs(days);
      return {
      days,
      tone: "expired",
      title: "Subscription has expired",
      message: `Expired ${elapsed} day${elapsed === 1 ? "" : "s"} ago. Renew now to restore uninterrupted access.`,
      badge: "Expired",
    };
  }

  if (days === 0) {
    return {
      days,
      tone: "urgent",
      title: "Subscription renewal due",
      message: "Renew today to avoid interruption to your POS workspace.",
      badge: "Ends today",
    };
  }

  if (days > reminderWindowDays) return null;

  return {
    days,
    tone: "soon",
    title: "Subscription expires soon",
    message: `Your POS workspace is active until ${formatDate(endDate)}.`,
    badge: `${days} day${days === 1 ? "" : "s"} left`,
  };
};

const toneClasses = {
  expired: "border-rose-200 bg-rose-50 text-rose-950",
  urgent: "border-amber-200 bg-amber-50 text-amber-950",
  soon: "border-amber-200 bg-amber-50 text-amber-950",
};

const badgeClasses = {
  expired: "bg-rose-100 text-rose-700",
  urgent: "bg-amber-100 text-amber-800",
  soon: "bg-amber-100 text-amber-800",
};

export default function SubscriptionCountdownBanner() {
  const bannerRef = React.useRef(null);
  const [dismissed, setDismissed] = React.useState(false);
  const role = useUserRole();
  const { user, customerId } = useAuth();
  const getCustomerData = useCustomerData();
  const customerData = useSelector((state) => state.auth.customer_data);
  const { currentTheme } = useTheme();
  const customer = customerData || (customerId ? getCustomerData() : null);
  const isManager = String(user?.role_name || "").toLowerCase() === "manager";
  const canOpenBilling = role === 1 && !isManager;
  const countdown = getCountdown(customer?.subscription_end_date);
  const dismissalKey = customer?.subscription_end_date
    ? `subscription_countdown_banner_dismissed:${user?.id || "admin"}:${customerId || customer?.id || "customer"}:${String(customer.subscription_end_date).slice(0, 10)}`
    : "";
  const shouldShow = role === 1 && Boolean(customer?.subscription_end_date) && Boolean(countdown) && !dismissed;

  React.useEffect(() => {
    if (!dismissalKey) {
      setDismissed(false);
      return;
    }
    setDismissed(sessionStorage.getItem(dismissalKey) === "true");
  }, [dismissalKey]);

  React.useEffect(() => {
    const root = document.documentElement;
    if (!shouldShow || !bannerRef.current) {
      root.style.setProperty("--admin-sticky-offset", "0px");
      return undefined;
    }

    const updateOffset = () => {
      const height = bannerRef.current?.offsetHeight || 0;
      root.style.setProperty("--admin-sticky-offset", `${height + 12}px`);
    };

    updateOffset();
    const observer = new ResizeObserver(updateOffset);
    observer.observe(bannerRef.current);
    window.addEventListener("resize", updateOffset);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateOffset);
      root.style.setProperty("--admin-sticky-offset", "0px");
    };
  }, [shouldShow]);

  if (!shouldShow) return null;

  const Icon = countdown.days <= 5 ? AlertTriangle : CalendarClock;

  const openBilling = () => {
    window.dispatchEvent(new CustomEvent("admin-open-billing"));
  };

  const dismissBanner = () => {
    if (dismissalKey) {
      sessionStorage.setItem(dismissalKey, "true");
    }
    setDismissed(true);
  };

  return (
    <section ref={bannerRef} className={`relative sticky top-0 z-40 mb-3 rounded-2xl border px-3 py-2.5 shadow-sm backdrop-blur sm:mb-4 sm:px-5 sm:py-3 ${toneClasses[countdown.tone]}`}>
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between md:gap-3 md:pr-12">
        <div className="flex min-w-0 items-center gap-2.5 pr-8 sm:gap-3 md:pr-0">
          <span
            className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-white shadow-sm sm:h-8 sm:w-8"
            style={{ background: currentTheme.color }}
          >
            <Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
              <h2 className="text-xs font-semibold sm:text-base">{countdown.title}</h2>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold sm:px-2.5 sm:py-1 sm:text-xs ${badgeClasses[countdown.tone]}`}>
                {countdown.badge}
              </span>
            </div>
            <p className="mt-0.5 line-clamp-2 text-[11px] leading-4 text-slate-600 sm:mt-1 sm:text-sm sm:leading-5">
              {countdown.message} <span className="text-slate-500">Billing period: {formatDate(customer.subscription_start_date)} to {formatDate(customer.subscription_end_date)}.</span>
            </p>
          </div>
        </div>

        <div className="flex w-full min-w-0 shrink-0 md:ml-3 md:w-auto">
          {canOpenBilling && (
            <button
              type="button"
              onClick={openBilling}
              className="inline-flex min-h-9 w-full min-w-0 flex-1 items-center justify-center gap-2 rounded-xl px-3 text-xs font-semibold text-white shadow-sm transition brightness-100 hover:brightness-95 sm:min-h-10 sm:px-4 sm:text-sm md:w-auto md:flex-none"
              style={{ background: currentTheme.color }}
            >
              <CreditCard className="h-4 w-4" />
              Open billing
            </button>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={dismissBanner}
        className="absolute right-2.5 top-2.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-black/10 bg-white/75 text-slate-600 shadow-sm transition hover:bg-white hover:text-slate-900 sm:right-3 sm:top-3 sm:h-10 sm:w-10 sm:rounded-xl"
        aria-label="Dismiss subscription reminder"
        title="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </section>
  );
}
